// frontend/src/components/masters/ShippingLineForm.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import
import './ShippingLineForm.css'; // Component-specific styles for the form
import { openFilePreview } from '../common/FilePreviewModal';
import EmailInput from '../common/EmailInput';
import { validateFormEmails } from '../common/EmailValidation';
import Select2Dropdown from '../common/Select2Dropdown';

const API_BASE_URL = '/api/masters/shippinglines/';
const BANKS_API_URL = '/api/masters/banks/';
const COUNTRIES_API_URL = '/api/masters/countries/';

const GEONAMES_USERNAME = 'salimh.'; // Your GeoNames username

// Helper to get auth token
const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    // For FormData, do not set Content-Type, let the browser do it.
    return headers;
};

const ShippingLineForm = ({ mode, initialData, onClose }) => {
    // Form state
    const [shippingLine, setShippingLine] = useState(initialData || {
        type: '', name: '', short_name: '', phone: '', address: 'P.O.BOX ', city: '', country: 'TZ',
        tin: '', tin_document: null, tax_invoice_has_receipt: true, vrn: '', vrn_document: null, vrn_na: false,
        contact_person: '', contact_phone: '',
        has_deposit_waiver: false,
        free_days: 14,
        bl_carriers: [], email_groups: [], tariffs: [], attachments: [],
        bank_details: [{ bank: '', swift_code: '', currency: 'TZS', account_number: '' }]
    });
    const [notes, setNotes] = useState([]);
    const [readOnly, setReadOnly] = useState(mode === 'view');
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [banks, setBanks] = useState([]);
    const [emailValidationErrors, setEmailValidationErrors] = useState({});
    const { hasPermission } = usePermissions(); // 2. Get hook

    // Refs for file inputs and preview modal
    const tinDocumentRef = useRef(null);
    const vrnDocumentRef = useRef(null);
    const attachmentRefs = useRef([]); // For multiple attachment files

    const previewModalRef = useRef(null);
    const previewIframeRef = useRef(null);
    const previewImageRef = useRef(null);
    const previewDownloadDivRef = useRef(null);
    const [isMaximized, setIsMaximized] = useState(false);

    // Initial data effect
    useEffect(() => {
            if (initialData) {
                // Ensure address has the prefix if it's missing or empty from initialData
                const addressToSet = (!initialData.address || initialData.address === '')
                ? 'P.O.BOX '
                : initialData.address.startsWith('P.O.BOX') 
                    ? initialData.address 
                    : `P.O.BOX ${initialData.address}`;
    
                setShippingLine({
                    ...initialData,
                    vrn_na: !!initialData.vrn_na,                    tax_invoice_has_receipt: initialData.tax_invoice_has_receipt !== undefined ? initialData.tax_invoice_has_receipt : true,                    address: addressToSet,
                    country: initialData.country || 'TZ',
                    bank_details: initialData.bank_details && initialData.bank_details.length > 0 ? initialData.bank_details : [{ bank: '', swift_code: '', currency: 'TZS', account_number: '' }]
                });

                // Load notes from initialData
                setNotes((initialData.notes || []).map(n => ({
                    ...n,
                    subject: (n.subject || '').toUpperCase(),
                    notes: (n.notes || '').toUpperCase()
                })));
            } else {
                // For new forms, ensure address starts with the prefix
                setShippingLine(prev => ({
                    ...prev,
                    address: 'P.O.BOX '
                }));
            }
            setReadOnly(mode === 'view');
        }, [mode, initialData]);

    // Resolve stored country name -> CCA2 once countries list is loaded; normalize city case
    useEffect(() => {
        if (countries.length > 0 && initialData?.country) {
            const found = countries.find(
                c => c.name.toUpperCase() === initialData.country.toUpperCase() || c.cca2 === initialData.country
            );
            if (found) {
                setShippingLine(prev => ({
                    ...prev,
                    country: found.cca2,
                    city: prev.city ? prev.city.toUpperCase() : prev.city,
                }));
            }
        }
    }, [countries, initialData]);

    // --- Geolocation, Phone, TIN, VRN Formatting ---
    const loadCountries = useCallback(async () => {
        try {
            const token = localStorage.getItem('kk_token');
            const response = await fetch(COUNTRIES_API_URL, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setCountries(Array.isArray(data) ? data : (data.results || []));
        } catch (err) {
            console.error('Failed to load countries:', err);
            setError('Failed to load countries.');
        }
    }, []);

    const loadBanks = useCallback(async () => {
        try {
            const response = await fetch(BANKS_API_URL, { headers: getAuthHeaders() });
            if (response.ok) {
                const data = await response.json();
                setBanks(data.results || data);
            } else {
                console.error("Failed to fetch banks");
            }
        } catch (err) {
            console.error("Error fetching banks:", err);
        }
    }, []);

    const updateCities = useCallback(async (countryCode) => {
        if (!countryCode || countryCode.length !== 2) {
            setCities([]);
            return;
        }
        try {
            const response = await fetch(`https://secure.geonames.org/searchJSON?country=${countryCode}&featureClass=P&maxRows=1000&username=${GEONAMES_USERNAME}`);
            const data = await response.json();
            if (data.geonames && data.geonames.length > 0) {
                const cityList = data.geonames.map(city => city.name).sort();
                setCities(cityList);
            } else {
                setCities([]);
            }
        } catch (err) {
            console.error("Failed to load cities:", err);
            setError('Failed to load cities.');
        }
    }, []);

    const countryPhoneCodes = {
        "TZ": "+255", "KE": "+254", "UG": "+256", "RW": "+250", "BI": "+257", // Add more as needed
    };

    const formatPhoneInput = useCallback((value) => {
        const countryCode = shippingLine.country;
        const code = countryPhoneCodes[countryCode] || "+";
        let digits = value.replace(new RegExp(`^\\${code}-?`), "").replace(/\D/g, "");
        let formatted = code + "-";
        if (digits.length > 0) formatted += digits.slice(0, 3);
        if (digits.length > 3) formatted += "-" + digits.slice(3, 6);
        if (digits.length > 6) formatted += "-" + digits.slice(6, 9);
        return formatted;
    }, [shippingLine.country]);

    const formatTIN = useCallback((value) => {
        let digits = value.replace(/\D/g, '').slice(0, 9);
        let formatted = '';
        if (digits.length > 0) formatted += digits.slice(0, 3);
        if (digits.length > 3) formatted += '-' + digits.slice(3, 6);
        if (digits.length > 6) formatted += '-' + digits.slice(6, 9);
        return formatted;
    }, []);

    const formatVRN = useCallback((value) => {
        let val = value.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
        let formatted = '';
        if (val.length > 0) formatted = val.slice(0, 2);
        if (val.length > 2) formatted += '-' + val.slice(2, 8);
        if (val.length > 8) formatted += '-' + val.slice(8, 9);
        return formatted;
    }, []);

    const enforceUppercaseInputs = useCallback(() => {
        document.querySelectorAll('.shipping-line-form-container input[type="text"], .shipping-line-form-container textarea').forEach(input => {
            if (!input.dataset.uppercaseListener) {
                input.addEventListener('input', function () {
                    this.value = this.value.toUpperCase();
                });
                input.dataset.uppercaseListener = "true";
            }
        });
    }, []);

    // Effects for data loading and formatting
    useEffect(() => {
        loadCountries();
        loadBanks();
    }, [loadCountries, loadBanks]);

    useEffect(() => {
        if (shippingLine.country) {
            // Set phone code only in add mode, or if phone is empty
            if (mode === 'add') {
                const code = countryPhoneCodes[shippingLine.country] || "+";
                if (!shippingLine.phone.startsWith(code + "-") || shippingLine.phone === '') {
                     setShippingLine(prev => ({ ...prev, phone: code + "-" + prev.phone.replace(/^\+\d+-?/, '') }));
                }
            }
            updateCities(shippingLine.country);
        }
    }, [shippingLine.country, updateCities, mode]);

    useEffect(() => {
        if (mode === 'add' && (!shippingLine.address || shippingLine.address === '')) {
            setShippingLine(prev => ({ ...prev, address: 'P.O.BOX ' }));
        }
    }, [mode, shippingLine.address]);


    // --- Form Field Handlers ---
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (readOnly) return;

        if (name === 'vrn_na') {
            setShippingLine(prev => ({
                ...prev,
                vrn_na: checked,
                vrn: checked ? '' : prev.vrn // Clear VRN if N/A checked
            }));
            return;
        }

        // Skip address handling here since we have a custom handler
        if (name === 'address') return;

        if (type === 'checkbox') {
            setShippingLine(prev => ({ ...prev, [name]: checked }));
        } else if (name === 'phone') {
            setShippingLine(prev => ({ ...prev, phone: formatPhoneInput(value) }));
        } else if (name === 'tin') {
            setShippingLine(prev => ({ ...prev, tin: formatTIN(value) }));
        } else if (name === 'vrn') {
            setShippingLine(prev => ({ ...prev, vrn: formatVRN(value) }));
        } else {
            setShippingLine(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleRepeatedChange = (e, index, fieldName, subArrayName) => {
        if (readOnly) return;
        const { value } = e.target;

        if (subArrayName === 'bank_details' && fieldName === 'bank') {
            const selectedBank = banks.find(b => b.id === parseInt(value));
            setShippingLine(prev => {
                const updatedArray = [...prev.bank_details];
                updatedArray[index] = { 
                    ...updatedArray[index], 
                    bank: selectedBank ? selectedBank.id : '',
                    swift_code: selectedBank ? selectedBank.swift_code : ''
                };
                return { ...prev, [subArrayName]: updatedArray };
            });
            return;
        }

        setShippingLine(prev => {
            const updatedArray = [...prev[subArrayName]];
            updatedArray[index] = { 
                ...updatedArray[index], 
                [fieldName]: fieldName === 'carrier_name' ? value.toUpperCase() : value 
            };
            return { ...prev, [subArrayName]: updatedArray };
        });
    };

    const getDisplayAddress = (address) => {
        // Remove P.O.BOX prefix for display in the input field
        if (!address) return '';
        return address.replace(/^P\.O\.BOX\s*/i, '').trim();
    };

    const getFullAddress = (addressValue) => {
        // Add P.O.BOX prefix when saving
        if (!addressValue) return 'P.O.BOX ';
        const cleanAddress = addressValue.replace(/^P\.O\.BOX\s*/i, '').trim();
        return cleanAddress ? `P.O.BOX ${cleanAddress}` : 'P.O.BOX ';
    };

    const handleAddressChange = (e) => {
        if (readOnly) return;
        const inputValue = e.target.value;
        // Store with P.O.BOX prefix
        const fullAddress = getFullAddress(inputValue);
        setShippingLine(prev => ({ ...prev, address: fullAddress }));
    };

    const handleEmailGroupChange = (value, validationState, index) => {
        setShippingLine(prev => {
            const updatedArray = [...prev.email_groups];
            updatedArray[index] = { ...updatedArray[index], emails: value };
            return { ...prev, email_groups: updatedArray };
        });

        // Update validation errors
        setEmailValidationErrors(prev => ({
            ...prev,
            [`email_groups_${index}_emails`]: validationState.errors
        }));
    };

    const handleFileChange = (e, fieldName, index = null) => {
        if (readOnly) return;
        const file = e.target.files[0];
        if (!file) return;

        if (fieldName === 'tin_document' || fieldName === 'vrn_document') {
            setShippingLine(prev => ({ ...prev, [fieldName]: file }));
        } else if (fieldName === 'attachments' && index !== null) {
            setShippingLine(prev => {
                const updatedAttachments = [...prev.attachments];
                updatedAttachments[index] = { ...updatedAttachments[index], file_obj: file, document_file: URL.createObjectURL(file) };
                return { ...prev, attachments: updatedAttachments };
            });
        }
    };

    // --- Repeatable Section Logic ---
    const addRepeatedItem = (subArrayName, newItem) => {
        if (readOnly) return;
        setShippingLine(prev => ({
            ...prev,
            [subArrayName]: [...prev[subArrayName], newItem]
        }));
    };

    const removeRepeatedItem = (subArrayName, index) => {
        if (readOnly) return;
        setShippingLine(prev => {
            const updatedArray = prev[subArrayName].filter((_, i) => i !== index);
            return { ...prev, [subArrayName]: updatedArray };
        });
    };

    // --- Notes Logic ---
    const addNote = () => {
        if (readOnly) return;
        const currentUserId = localStorage.getItem('kk_user_id');
        setNotes([...notes, {
            subject: '',
            notes: '',
            note_date: new Date().toISOString().split('T')[0],
            created_by_user: currentUserId
        }]);
    };

    const saveNotes = async (shippingLineId) => {
        try {
            const payload = {
                shipping_line_id: shippingLineId,
                notes: notes.map(n => ({
                    id: n.id,
                    subject: (n.subject || '').toUpperCase(),
                    notes: (n.notes || '').toUpperCase(),
                    note_date: n.note_date,
                    created_by_user: n.created_by_user
                }))
            };
            const response = await fetch('/api/masters/shipping-line-notes/bulk_create/', {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });
            return response.ok;
        } catch (err) {
            console.error('Error saving notes:', err);
            return false;
        }
    };

    // --- File Preview Modal Logic (reused from CustomerForm) ---
    const openPreview = (fileURL, fileName) => {
        openFilePreview(fileURL, fileName, {
            customTitle: `Preview: ${fileName || 'Document'}`,
            onDownload: (data) => {
                // Custom download handler if needed
                console.log('Download requested:', data);
            },
            onClose: () => {
                // Custom close handler if needed
                console.log('Preview window closed');
            }
        });
    };

    const closePreview = () => {
        if (previewModalRef.current && previewIframeRef.current && previewImageRef.current && previewDownloadDivRef.current) {
            previewModalRef.current.style.display = 'none';
            previewIframeRef.current.src = '';
            previewImageRef.current.src = '';
            previewDownloadDivRef.current.style.display = 'none';
            setIsMaximized(false); // Reset maximize state
            Object.assign(previewModalRef.current.style, {
                top: previewModalRef.current.dataset.originalTop || '50px',
                left: previewModalRef.current.dataset.originalLeft || '50px',
                width: previewModalRef.current.dataset.originalWidth || '80%',
                height: previewModalRef.current.dataset.originalHeight || '80%',
                resize: 'both'
            });
        }
    };

    const toggleMaximize = () => {
        if (!previewModalRef.current) return;
        const modal = previewModalRef.current;
        const btn = modal.querySelector('#maximizeBtn');

        if (!isMaximized) {
            modal.dataset.originalLeft = modal.style.left;
            modal.dataset.originalTop = modal.style.top;
            modal.dataset.originalWidth = modal.style.width;
            modal.dataset.originalHeight = modal.style.height;

            modal.style.top = '0';
            modal.style.left = '0';
            modal.style.width = '100vw';
            modal.style.height = '100vh';
            modal.style.resize = 'none';
            btn.textContent = 'Restore';
        } else {
            modal.style.top = modal.dataset.originalTop;
            modal.style.left = modal.dataset.originalLeft;
            modal.style.width = modal.dataset.originalWidth;
            modal.style.height = modal.dataset.originalHeight;
            modal.style.resize = 'both';
            btn.textContent = 'Maximize';
        }
        setIsMaximized(!isMaximized);
    };

    // Make modal draggable
    useEffect(() => {
        const modal = previewModalRef.current;
        if (!modal) return;
        const header = modal.querySelector('#modalHeader');
        let isDragging = false;
        let offsetX, offsetY;

        const onMouseDown = (e) => {
            isDragging = true;
            offsetX = e.clientX - modal.offsetLeft;
            offsetY = e.clientY - modal.offsetTop;
            document.body.style.userSelect = 'none';
        };
        const onMouseUp = () => {
            isDragging = false;
            document.body.style.userSelect = 'auto';
        };
        const onMouseMove = (e) => {
            if (isDragging) {
                modal.style.left = (e.clientX - offsetX) + 'px';
                modal.style.top = (e.clientY - offsetY) + 'px';
            }
        };

        if (header) {
            header.addEventListener('mousedown', onMouseDown);
            document.addEventListener('mouseup', onMouseUp);
            document.addEventListener('mousemove', onMouseMove);
        }

        return () => {
            if (header) {
                header.removeEventListener('mousedown', onMouseDown);
                document.removeEventListener('mouseup', onMouseUp);
                document.removeEventListener('mousemove', onMouseMove);
            }
        };
    }, []);

    // --- Form Submission ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (readOnly) return;

        setError(null);
        setMessage(null);

        // --- BL Carrier Validation ---
        if (!shippingLine.bl_carriers || shippingLine.bl_carriers.length === 0) {
            setError('Please add at least one BL Carrier before saving.');
            toast.error('Please add at least one BL Carrier before saving.');
            return;
        }

        const formData = new FormData();

        // Validate all email groups
        const emailFields = shippingLine.email_groups.map((group, index) => ({
            fieldPath: `email_groups.${index}.emails`,
            required: false, // Set to true if you want to make emails required
            label: `Email Group ${index + 1}`
        }));

        const emailValidation = validateFormEmails(shippingLine, emailFields);
        
        if (!emailValidation.isValid) {
            setEmailValidationErrors(emailValidation.errors);
            setError('Please fix email validation errors before submitting.');
            toast.error('Please fix email validation errors before submitting.');
            return;
        }

        // Clear email validation errors if all are valid
        setEmailValidationErrors({});

        // Append simple fields
        for (const key in shippingLine) {
            if (['bl_carriers', 'email_groups', 'tariffs', 'attachments', 'tin_document', 'vrn_document'].includes(key)) {
                continue;
            }
            if (key === 'has_deposit_waiver' && shippingLine.line_type === 'AGENT') {
                formData.append(key, '');
            } else if (key === 'free_days' && shippingLine.line_type === 'AGENT') {
                // Free days only applies to SHIPPING LINE — don't send for agents
                continue;
            } else if (key === 'free_days') {
                formData.append(key, shippingLine[key] === '' ? 14 : shippingLine[key]);
            } else if (typeof shippingLine[key] === 'boolean') {
                formData.append(key, shippingLine[key] ? 'true' : 'false');
            } else if (key === 'country' && shippingLine[key] !== null && shippingLine[key] !== undefined) {
                const countryObject = countries.find(c => c.cca2 === shippingLine[key]);
                formData.append(key, countryObject ? countryObject.name : shippingLine[key]);
            } else if (shippingLine[key] !== null && shippingLine[key] !== undefined) {
                formData.append(key, shippingLine[key]);
            }
        }

        // Append file fields
        if (shippingLine.tin_document instanceof File) {
            formData.append('tin_document', shippingLine.tin_document);
        } else if (typeof shippingLine.tin_document === 'string' && shippingLine.tin_document === initialData?.tin_document) {
            // Keep existing if unchanged and still a string (URL)
            // No need to append if DRF handles absence as 'keep existing'
        } else if (shippingLine.tin_document === null && initialData?.tin_document) {
            // If explicitly cleared by user, you might need a flag or specific value for backend to handle deletion.
            // For now, if null and was existing, implies deletion/clearing if backend's serializer is configured for it.
            // Otherwise, typically it means 'no new file uploaded, keep existing'.
            // Default to not sending if it's null, assuming backend default behavior.
        }


        if (shippingLine.vrn_document instanceof File) {
            formData.append('vrn_document', shippingLine.vrn_document);
        } else if (typeof shippingLine.vrn_document === 'string' && shippingLine.vrn_document === initialData?.vrn_document) {
            // Keep existing
        } else if (shippingLine.vrn_document === null && initialData?.vrn_document) {
            // Explicitly mark for removal if backend expects it this way
        }

        formData.append('vrn_na', shippingLine.vrn_na ? 'true' : 'false');
        // Append nested arrays (convert to JSON string)
        formData.append('bl_carriers', JSON.stringify(shippingLine.bl_carriers));
        formData.append('email_groups', JSON.stringify(shippingLine.email_groups));
        formData.append('tariffs', JSON.stringify(shippingLine.tariffs));
        formData.append('bank_details', JSON.stringify(shippingLine.bank_details));

        shippingLine.attachments.forEach((attachment, index) => {
            if (attachment.file_obj instanceof File) {
                formData.append(`attachments[${index}]document_file`, attachment.file_obj);
            }
            formData.append(`attachments[${index}]attachment_type`, attachment.attachment_type || '');
            if (attachment.id && !(attachment.file_obj instanceof File)) {
                formData.append(`attachments[${index}]id`, attachment.id);
            }
        });

        const url = initialData ? `${API_BASE_URL}${initialData.id}/` : API_BASE_URL;
        const method = initialData ? 'PUT' : 'POST';

        try {
                const response = await fetch(url, {
                    method: method,
                    headers: getAuthHeaders(true),
                    body: formData,
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    const errorMessage = Object.entries(errorData).map(([key, val]) => `${key}: ${val}`).join('; ');
                    setError(`Save failed: ${errorMessage}`);
                    toast.error(`Save failed: ${errorMessage}`);
                    return;
                }

                const savedData = await response.json();
                const shippingLineId = savedData.id || initialData?.id;

                // Save notes if any
                if (notes.length > 0 && shippingLineId) {
                    await saveNotes(shippingLineId);
                }

                toast.success('Shipping Line saved successfully!');
                if (onClose) onClose(true);

            } catch (err) {
                setError('Failed to save Shipping Line. Network error or server unreachable.');
                toast.error('Failed to save Shipping Line. Network error or server unreachable.');
            }
    };

    const handleCancel = () => {
        onClose(false); // Notify parent to close without refreshing list
    };

    return (
        <div className="shipping-line-form-container">
            <h1>Shipping Line Details</h1>
            <div className="toggle-buttons">
                <button onClick={() => document.querySelectorAll('.section-content').forEach(el => el.style.display = 'block')}>Expand All</button>
                <button onClick={() => document.querySelectorAll('.section-content').forEach(el => el.style.display = 'none')}>Collapse All</button>
            </div>

            {error && <div className="form-error">{error}</div>}
            {message && <div className="form-success">{message}</div>}

            <form onSubmit={handleSubmit}>
                {/* Shipping Line Information */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Shipping Line Information</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="row">
                            <div style={{width: '50%'}}>
                                <label className="required">Type</label>
                                <select name="line_type" value={shippingLine.line_type} onChange={handleChange} required disabled={readOnly}>
                                    <option value="">SELECT</option>
                                    <option value="SHIPPING LINE">SHIPPING LINE / AIRLINE</option>
                                    <option value="AGENT">LOCAL AGENT/CONSOLIDATOR</option>
                                </select>
                                {shippingLine.line_type === 'SHIPPING LINE' && (
                                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
                                        <label htmlFor="has_deposit_waiver" style={{ margin: '0 10px 0 0' }}>Has Deposit Waiver</label>
                                        <input type="checkbox" name="has_deposit_waiver" id="has_deposit_waiver" checked={shippingLine.has_deposit_waiver} onChange={handleChange} disabled={readOnly} style={{ width: 'auto', margin: 0 }} />
                                        <label htmlFor="free_days" style={{ margin: '0 10px 0 20px' }}>Free Days</label>
                                        <input
                                            type="number"
                                            name="free_days"
                                            id="free_days"
                                            min="0"
                                            value={shippingLine.free_days ?? 14}
                                            onChange={handleChange}
                                            disabled={readOnly || !hasPermission('SHIPPING LINES', 'can_delete')}
                                            style={{ width: '80px' }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="row" style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.75fr 1fr', gap: '20px' }}>
                            <div><label className="required">Name</label><input type="text" name="name" value={shippingLine.name} onChange={handleChange} required readOnly={readOnly} /></div>
                            <div><label>Short Name</label><input type="text" name="short_name" value={shippingLine.short_name || ''} onChange={handleChange} readOnly={readOnly} maxLength="50" /></div>
                            <div><label className="required">Phone</label><input type="text" name="phone" value={shippingLine.phone} onChange={handleChange} required readOnly={readOnly} placeholder="+255-___-___-___" maxLength={shippingLine.country ? (countryPhoneCodes[shippingLine.country] || "+").length + 12 : 17} /></div>
                        <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '20px' }}>
                            <div>
                                <label className="required">Address</label>
                                <div className={`address-input-container ${readOnly ? 'readonly' : ''}`}>
                                    <span className="address-prefix">P.O.BOX</span>
                                    <input 
                                            type="text" 
                                            name="address" 
                                            value={getDisplayAddress(shippingLine.address)}
                                            onChange={handleAddressChange}
                                            required 
                                            readOnly={readOnly}
                                            className="address-input"
                                            placeholder="Enter address details..."
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="required">Country</label>
                                    <Select2Dropdown
                                        options={countries.map(c => ({ id: c.cca2, text: c.name }))}
                                        value={shippingLine.country}
                                        onChange={value => {
                                            setShippingLine(prev => ({ ...prev, country: value }));
                                            updateCities(value);
                                        }}
                                        placeholder="SELECT COUNTRY"
                                        displayKey="text"
                                        valueKey="id"
                                        required
                                        disabled={readOnly}
                                        clearable={true}
                                    />
                                </div>
                                <div>
                                    <label className="required">City</label>
                                    <Select2Dropdown
                                        options={cities.map(c => ({ id: c.toUpperCase(), text: c.toUpperCase() }))}
                                        value={shippingLine.city}
                                        onChange={value => setShippingLine(prev => ({ ...prev, city: value }))}
                                        placeholder="SELECT CITY"
                                        displayKey="text"
                                        valueKey="id"
                                        required
                                        disabled={readOnly}
                                        clearable={true}
                                    />
                                </div>
                            </div>
                            <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 2fr 2fr 1fr', gap: '20px' }}>
                                <div>
                                    <label className="required">TIN</label>
                                    <input type="text" name="tin" value={shippingLine.tin} onChange={handleChange} required readOnly={readOnly} pattern="\d{3}-\d{3}-\d{3}" maxLength="11" placeholder="123-456-789" />
                                </div>
                                <div>
                                    <label className="required">TIN Document Upload</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input type="file" ref={tinDocumentRef} onChange={(e) => handleFileChange(e, 'tin_document')} accept=".pdf,.jpg,.png,.doc,.docx" disabled={readOnly} style={{ width: '200px' }} />
                                        {(shippingLine.tin_document || initialData?.tin_document) && (
                                            <div className="file-links" style={{ whiteSpace: 'nowrap', display: 'inline' }}>
                                                {typeof shippingLine.tin_document === 'string' && shippingLine.tin_document && <a href={shippingLine.tin_document} target="_blank" rel="noopener noreferrer" className="preview-link">View Existing</a>}
                                                {shippingLine.tin_document instanceof File && <span onClick={() => openPreview(URL.createObjectURL(shippingLine.tin_document), shippingLine.tin_document.name)} className="preview-link">View Uploaded</span>}
                                                {shippingLine.tin_document instanceof File && <a href={URL.createObjectURL(shippingLine.tin_document)} download={shippingLine.tin_document.name}>Download</a>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label>Contact Person</label>
                                    <input type="text" name="contact_person" value={shippingLine.contact_person} onChange={handleChange} readOnly={readOnly} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', marginTop: '30px' }}>
                                    <label htmlFor="tax_invoice_has_receipt" style={{ marginRight: '10px', whiteSpace: 'nowrap' }}>Tax Invoice Has Receipt</label>
                                    <input 
                                        type="checkbox" 
                                        name="tax_invoice_has_receipt" 
                                        id="tax_invoice_has_receipt" 
                                        checked={shippingLine.tax_invoice_has_receipt} 
                                        onChange={handleChange} 
                                        disabled={readOnly} 
                                        style={{ width: 'auto', margin: 0 }} 
                                    />
                                </div>
                            </div>
                            <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 2fr 2fr 1fr', gap: '20px' }}>
                                <div>
                                    <label className="required">VRN</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input
                                            type="text"
                                            name="vrn"
                                            value={shippingLine.vrn}
                                            onChange={handleChange}
                                            required={!shippingLine.vrn_na}
                                            readOnly={readOnly || shippingLine.vrn_na}
                                            pattern="\d{2}-\d{6}-[A-Z]"
                                            maxLength="11"
                                            placeholder="12-345678-A"
                                        />
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'normal' }}>
                                            <input
                                                type="checkbox"
                                                name="vrn_na"
                                                checked={!!shippingLine.vrn_na}
                                                onChange={handleChange}
                                                disabled={readOnly}
                                                style={{ marginLeft: '8px', cursor: readOnly ? 'not-allowed' : 'pointer' }}
                                            />
                                            N/A
                                        </label>
                                    </div>
                                </div>
                                <div>
                                    <label className="required">VRN Document Upload</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <input type="file" ref={vrnDocumentRef} onChange={(e) => handleFileChange(e, 'vrn_document')} accept=".pdf,.jpg,.png,.doc,.docx" disabled={readOnly} style={{ width: '200px' }} />
                                        {(shippingLine.vrn_document || initialData?.vrn_document) && (
                                            <div className="file-links" style={{ whiteSpace: 'nowrap', display: 'inline' }}>
                                                {typeof shippingLine.vrn_document === 'string' && shippingLine.vrn_document && <a href={shippingLine.vrn_document} target="_blank" rel="noopener noreferrer" className="preview-link">View Existing</a>}
                                                {shippingLine.vrn_document instanceof File && <span onClick={() => openPreview(URL.createObjectURL(shippingLine.vrn_document), shippingLine.vrn_document.name)} className="preview-link">View Uploaded</span>}
                                                {shippingLine.vrn_document instanceof File && <a href={URL.createObjectURL(shippingLine.vrn_document)} download={shippingLine.vrn_document.name}>Download</a>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label>Contact Phone</label>
                                    <input type="text" name="contact_phone" value={shippingLine.contact_phone} onChange={handleChange} readOnly={readOnly} />
                                </div>
                                <div></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* BL Carriers */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>BL Carriers</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="repeatable" id="blCarriers">
                            {shippingLine.bl_carriers.map((carrier, index) => (
                                <div key={index} className="repeatable-row">
                                    <div style={{ flex: '0.5' }}><label className="required">BL Carrier</label><input type="text" value={carrier.carrier_name || ''} onChange={(e) => handleRepeatedChange(e, index, 'carrier_name', 'bl_carriers')} required readOnly={readOnly} /></div>
                                    {!readOnly && <button type="button" onClick={() => removeRepeatedItem('bl_carriers', index)} className="remove-btn">Remove</button>}
                                </div>
                            ))}
                        </div>
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('bl_carriers', { carrier_name: '' })}>Add BL Carrier</button>}
                    </div>
                </div>

                {/* Bank Details Section */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Bank Details</h2>
                    <div className="section-content">
                        <div className="repeatable" id="bank_details">
                            {shippingLine.bank_details.map((detail, index) => (
                                <div key={index} className="repeatable-row">
                                    <div style={{ flex: '2' }}>
                                        <label className="required">Bank</label>
                                        <Select2Dropdown
                                            options={banks.map(b => ({ id: b.id, text: b.name }))}
                                            value={detail.bank}
                                            onChange={val => handleRepeatedChange({ target: { value: val } }, index, 'bank', 'bank_details')}
                                            valueKey="id"
                                            displayKey="text"
                                            placeholder="Select Bank"
                                            disabled={readOnly}
                                        />
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        <label>Swift Code</label>
                                        <input type="text" value={detail.swift_code || ''} readOnly placeholder="Auto-populated" />
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        <label className="required">Currency</label>
                                        <Select2Dropdown
                                            options={majorCurrencies}
                                            value={detail.currency}
                                            onChange={val => handleRepeatedChange({ target: { value: val } }, index, 'currency', 'bank_details')}
                                            valueKey="id"
                                            displayKey="text"
                                            placeholder="Select Currency"
                                            disabled={readOnly}
                                        />
                                    </div>
                                    <div style={{ flex: '2' }}>
                                        <label className="required">Account Number</label>
                                        <input type="text" value={detail.account_number || ''} onChange={(e) => handleRepeatedChange(e, index, 'account_number', 'bank_details')} required readOnly={readOnly} />
                                    </div>
                                    {!readOnly && <button type="button" onClick={() => removeRepeatedItem('bank_details', index)} className="remove-btn">Remove</button>}
                                </div>
                            ))}
                        </div>
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('bank_details', { bank: '', swift_code: '', currency: 'TZS', account_number: '' })}>Add Bank Detail</button>}
                    </div>
                </div>

                {/* Shipping Line Email Groups */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Shipping Line Email Groups</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="repeatable" id="emailGroups">
                            {shippingLine.email_groups.map((group, index) => (
                                <div key={index} className="repeatable-row">
                                    <div style={{ flex: '1' }}><label className="required">Email Group</label>
                                    <input type="text" value={group.group_name || ''} onChange={(e) => handleRepeatedChange(e, index, 'group_name', 'email_groups')} required readOnly={readOnly} /></div>
                                    <div style={{ flex: '2' }}><label className="required">Emails</label>
                                        <EmailInput
                                            value={group.emails}
                                            onChange={(value, validationState) => handleEmailGroupChange(value, validationState, index)}
                                            placeholder="Email addresses (separate with comma or semicolon)"
                                            readOnly={readOnly}
                                            required={false} // Set to true if you want to make emails required
                                            showValidationStatus={false}
                                            showHelpText={true}
                                        />
                                    </div>
                                    {!readOnly && <button type="button" onClick={() => removeRepeatedItem('email_groups', index)} className="remove-btn">Remove</button>}
                                </div>
                            ))}
                        </div>
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('email_groups', { group_name: '', emails: '' })}>Add Email Group</button>}
                    </div>
                </div>

                {/* Shipping Line Tariffs */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Shipping Line Tariffs</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="repeatable" id="tariffs">
                            {shippingLine.tariffs.map((tariff, index) => (
                                <div key={index} className="repeatable-row">
                                    <div style={{ flex: '1' }}>
                                        <label className="required">Tariff Name</label>
                                        <select value={tariff.tariff_name || ''} onChange={(e) => handleRepeatedChange(e, index, 'tariff_name', 'tariffs')} required disabled={readOnly}>
                                            <option value="">SELECT</option>
                                            <option value="Container Deposit">Container Deposit</option>
                                            <option value="TASAC FEES">TASAC FEES</option>
                                            <option value="NOMINATION FEES">NOMINATION FEES</option>
                                            <option value="DELIVERY ORDER FEES">DELIVERY ORDER FEES</option>
                                        </select>
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        <label className="required">Container Size</label>
                                        <select value={tariff.container_size || ''} onChange={(e) => handleRepeatedChange(e, index, 'container_size', 'tariffs')} required disabled={readOnly}>
                                            <option value="">SELECT</option>
                                            <option value="20 ST/GP">20 ST/GP</option>
                                            <option value="20 REFER">20 REFER</option>
                                            <option value="40 ST/GP">40 ST/GP</option>
                                            <option value="40 HC/HQ">40 HC/HQ</option>
                                            <option value="40 REFER">40 REFER</option>
                                        </select>
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        <label className="required">Currency</label>
                                        <select value={tariff.currency || 'USD'} onChange={(e) => handleRepeatedChange(e, index, 'currency', 'tariffs')} required disabled={readOnly}>
                                            <option value="TZS">TZS</option>
                                            <option value="USD">USD</option>
                                        </select>
                                    </div>
                                    <div style={{ flex: '1' }}><label className="required">Unit Rate</label><input type="number" step="0.01" value={tariff.unit_rate || ''} onChange={(e) => handleRepeatedChange(e, index, 'unit_rate', 'tariffs')} required readOnly={readOnly} /></div>
                                    {!readOnly && <button type="button" onClick={() => removeRepeatedItem('tariffs', index)} className="remove-btn">Remove</button>}
                                </div>
                            ))}
                        </div>
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('tariffs', { tariff_name: '', container_size: '', currency: 'USD', unit_rate: '' })}>Add Tariff</button>}
                    </div>
                </div>

                {/* Attachments */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Attachments</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        <div className="repeatable" id="attachments">
                            {shippingLine.attachments.map((attachment, index) => (
                                <div key={index} className="repeatable-row">
                                    <div style={{ flex: '0.5', minWidth: '90px', marginRight: '8px' }}>
                                        <label className="required">Type</label>
                                        <input type="text" value={attachment.attachment_type || ''} onChange={(e) => handleRepeatedChange(e, index, 'attachment_type', 'attachments')} required readOnly={readOnly} placeholder="Enter document type" />
                                    </div>
                                    <div style={{ flex: '3' }}>
                                        <label className="required">Document Upload</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <input type="file" ref={el => attachmentRefs.current[index] = el} onChange={(e) => handleFileChange(e, 'attachments', index)} accept=".pdf,.jpg,.png,.doc,.docx" disabled={readOnly} style={{ width: '200px' }} />
                                            {(attachment.document_file || attachment.file_obj) && (
                                                <div className="file-links" style={{ whiteSpace: 'nowrap', display: 'inline' }}>
                                                    {typeof attachment.document_file === 'string' && attachment.document_file && <a href={attachment.document_file} target="_blank" rel="noopener noreferrer" className="preview-link">View Existing</a>}
                                                    {attachment.file_obj instanceof File && <span onClick={() => openPreview(URL.createObjectURL(attachment.file_obj), attachment.file_obj.name)} className="preview-link">View Uploaded</span>}
                                                    {attachment.file_obj instanceof File && <a href={URL.createObjectURL(attachment.file_obj)} download={attachment.file_obj.name}>Download</a>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {!readOnly && <button type="button" onClick={() => removeRepeatedItem('attachments', index)} className="remove-btn">Remove</button>}
                                </div>
                            ))}
                        </div>
                        {!readOnly && <button type="button" onClick={() => addRepeatedItem('attachments', { attachment_type: '', document_file: null, file_obj: null })}>Add Attachment</button>}
                    </div>
                </div>

                {/* Notes Section */}
                <div className="section">
                    <h2 onClick={(e) => e.target.nextElementSibling.style.display = e.target.nextElementSibling.style.display === 'block' ? 'none' : 'block'}>Notes ({notes.length})</h2>
                    <div className="section-content" style={{ display: 'block' }}>
                        {notes.map((note, index) => (
                            <div key={index} className="repeatable-row">
                                <div>
                                    <label>Subject</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Subject"
                                        value={note.subject || ''}
                                        onChange={(e) => {
                                            const updated = [...notes];
                                            updated[index].subject = e.target.value.toUpperCase();
                                            setNotes(updated);
                                        }}
                                        readOnly={readOnly}
                                        style={{ textTransform: 'uppercase' }}
                                    />
                                </div>
                                <div>
                                    <label>Notes</label>
                                    <textarea
                                        placeholder="Enter Notes"
                                        value={note.notes || ''}
                                        onChange={(e) => {
                                            const updated = [...notes];
                                            updated[index].notes = e.target.value.toUpperCase();
                                            setNotes(updated);
                                        }}
                                        readOnly={readOnly}
                                        rows={3}
                                        style={{ textTransform: 'uppercase' }}
                                    />
                                </div>                                
                                <div>
                                    <label>Date</label>
                                    <input
                                        type="date"
                                        value={note.note_date || ''}
                                        onChange={(e) => {
                                            const updated = [...notes];
                                            updated[index].note_date = e.target.value;
                                            setNotes(updated);
                                        }}
                                        readOnly={true}
                                    />
                                </div>
                                <div>
                                    <label>Created By</label>
                                    <input
                                        type="text"
                                        value={localStorage.getItem('kk_user') || 'Unknown User'}
                                        readOnly={true}
                                        style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                </div>
                                {!readOnly && (
                                    <button type="button" onClick={() => setNotes(notes.filter((_, i) => i !== index))} className="remove-btn">
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                        {!readOnly && (
                            <button type="button" onClick={addNote}>Add Note</button>
                        )}
                    </div>
                </div>

                <div className="buttons">
                    {/* 3. Protect Edit button */}
                    {readOnly && hasPermission('SHIPPING LINES', 'can_edit') && (
                        <button type="button" onClick={() => setReadOnly(false)}>Edit</button>
                    )}
                    {/* 4. Protect Save button */}
                    {!readOnly && (
                        <button type="submit">Save</button>
                    )}
                    <button type="button" onClick={handleCancel} className="cancel-btn">Cancel</button>
                </div>
            </form>

            {/* File Preview Modal */}
            <div id="previewModal" ref={previewModalRef} style={{ display: 'none', position: 'fixed', top: '50px', left: '50px', width: '80%', height: '80%', backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '8px', zIndex: '9999', boxShadow: '0 0 10px rgba(0,0,0,0.5)', resize: 'both', overflow: 'auto' }}>
                <div id="modalHeader" style={{ cursor: 'move', background: '#f1f1f1', padding: '10px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>File Preview</span>
                    <div>
                        <button id="maximizeBtn" onClick={toggleMaximize} style={{ fontSize: '12px', marginRight: '5px' }}>Maximize</button>
                        <button onClick={closePreview} style={{ fontSize: '12px' }}>Close</button>
                    </div>
                </div>
                <iframe id="previewFrame" ref={previewIframeRef} style={{ width: '100%', height: 'calc(100% - 40px)', border: 'none', display: 'none' }} title="File Preview"></iframe>
                <img id="previewImage" ref={previewImageRef} style={{ maxWidth: '100%', maxHeight: 'calc(100% - 40px)', display: 'none', margin: '0 auto' }} alt="Preview" />
                <div id="downloadDiv" ref={previewDownloadDivRef} style={{ padding: '20px', textAlign: 'center', display: 'none' }}></div>
            </div>
        </div>
    );
};

const majorCurrencies = [  
        { id: 'EUR', text: 'EUR - Euro' },
        { id: 'GBP', text: 'GBP - British Pound Sterling' },
        { id: 'TZS', text: 'TZS - Tanzanian Shilling' },
        { id: 'USD', text: 'USD - United States Dollar' },
    ];

export default ShippingLineForm;
